<template>
    <div class="relative inline-block text-left">
        <template v-if="question">
            <AppButton
                v-if="canAddQuestions"
                element="button"
                @click="openCopyOfQuestion(question)"
                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
                <IconCopy class="w-4 h-4" />
            </AppButton>
            <AppButton
                v-else
                element="button"
                :title="$t('messages.cannot_add_questions_to_activity_that_already_has_attempts')"
                class="cursor-not-allowed inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
                <IconCopy class="w-4 h-4" />
            </AppButton>
        </template>
        <div v-else class="gap-2 flex flex-col">
            <span> {{ $t('messages.add') }}: </span>
            <div v-for="questionTypeGroup in questionTypeGroups" :key="questionTypeGroup.label">
                <div class="flex gap-2 flex-wrap">
                    <div v-for="questionType in questionTypeGroup.options" :key="questionType.value">
                        <AppButton
                            v-if="canAddQuestions"
                            element="button"
                            @click="openQuestionFor(questionType.value)"
                            type="button"
                        >
                            <QuestionIcon class="text-gray-400 w-4 h-4" :type="questionType.value" />

                            <span class="ml-1">{{ questionType.text }}</span>
                        </AppButton>
                        <AppButton
                            v-else
                            element="button"
                            type="button"
                            :disabled="true"
                            class="cursor-not-allowed"
                            :title="$t('messages.cannot_add_questions_to_activity_that_already_has_attempts')"
                        >
                            {{ questionType.text }}
                        </AppButton>
                    </div>
                </div>
            </div>
        </div>

        <div>
            <SlideOver :title="$t('messages.create')" size="xxl" :show="open" @close="open = false">
                <FormSelect v-model="form.type" :options="questionTypeGroups" optgroups />

                <form
                    :action="meta.links.update"
                    method="post"
                    @submit.prevent="submit"
                    v-if="form.type"
                    class="flex flex-col h-full"
                >
                    <div class="flex-1 md:grid md:grid-cols-12 md:gap-6 overflow-y-scroll">
                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.title')" for="title" />
                                <FormInput
                                    required
                                    id="title"
                                    v-model="form.title"
                                    name="title"
                                    ref="title"
                                    type="text"
                                    max="191"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.title" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.description')" for="description" />
                                <div v-auto-animate>
                                    <FormTextarea
                                        id="description"
                                        v-model="form.description"
                                        name="description"
                                        type="text"
                                        class="mt-1"
                                        v-if="form.is_latex"
                                    />
                                    <Editor
                                        id="description"
                                        v-model="form.description"
                                        name="description"
                                        class="mt-1"
                                        :only="['bold', 'italic']"
                                        v-else
                                    />
                                </div>
                                <FormError :value="form.errors.description" class="mt-2" />
                            </div>
                            <div class="mt-1">
                                <label class="mr-1">
                                    <input
                                        :value="false"
                                        name="is_latex"
                                        v-model="form.is_latex"
                                        type="radio"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                    />
                                    {{ $t('messages.formatted_text') }}
                                </label>
                                <label class="mr-1">
                                    <input
                                        :value="true"
                                        name="is_latex"
                                        v-model="form.is_latex"
                                        type="radio"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                    />
                                    <span>
                                        {{ $t('messages.is_latex') }}

                                        <span class="inline-block">
                                            <Tippy>
                                                <IconHelpCircle
                                                    class="h-6 w-6 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"
                                                />
                                                <template #content>
                                                    <span>{{ $t('messages.is_latex_explanation') }}</span>
                                                </template>
                                            </Tippy>
                                        </span>
                                    </span>
                                </label>
                            </div>
                            <div class="mt-1" v-auto-animate>
                                <AppWell v-if="form.is_latex">
                                    <LatexDisplay v-model="form.description" />
                                </AppWell>
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.hint')" for="hint">
                                    <template v-slot:corner>
                                        <span class="text-gray-500 text-sm"
                                            >({{ $t('messages.for_adaptations') }})</span
                                        >
                                    </template>
                                </FormLabel>
                                <FormInput
                                    id="hint"
                                    v-model="form.hint"
                                    name="hint"
                                    type="text"
                                    max="191"
                                    :placeholder="$t('messages.(optional)')"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.hint" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.image')" for="image" />
                                <input
                                    id="image"
                                    @input="form.image = $event.target.files[0]"
                                    name="image"
                                    type="file"
                                    accept="image/png,image/jpg,image/jpeg,"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.image" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.image_bank')" for="shared_image_name" />
                                <FormInput
                                    id="shared_image_name"
                                    v-model="form.shared_image_name"
                                    name="shared_image_name"
                                    list="shared_image_name_suggestions"
                                    type="text"
                                    max="191"
                                    :placeholder="$t('messages.(optional)')"
                                    class="mt-1 w-full"
                                />
                                <FormError :value="form.errors.shared_image_name" class="mt-2" />
                                <QuestionSharedImageSuggestions :search="form.shared_image_name" />
                            </div>
                        </div>

                        <div class="sm:col-span-6" v-if="canHaveLatexAnswers">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.is_latex_answers')" for="is_latex_answers" />
                                <input
                                    id="is_latex_answers"
                                    name="is_latex_answers"
                                    v-model="form.is_latex_answers"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <FormError :value="form.errors.is_latex_answers" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12 p-4 bg-gray-50 rounded-lg">
                            <QuizSpecificOptionsForm :type="form.type" v-model="form.options" :errors="form.errors" />
                        </div>

                        <div class="sm:col-span-6">
                            <FormLabel :value="$t('messages.weight')" for="weight">(1-5)</FormLabel>
                            <FormInput
                                required
                                id="weight"
                                v-model.number="form.weight"
                                name="weight"
                                type="number"
                                step="1"
                                max="5"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.weight" class="mt-2" />
                        </div>

                        <div class="sm:col-span-6">
                            <FormLabel :value="$t('messages.difficulty')" for="difficulty" />
                            <FormSmallNumberRangeInput v-model.number="form.difficulty" name="difficulty" :max="5" />
                            <FormError :value="form.errors.difficulty" class="mt-2" />
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.share_on_question_bank')" for="is_negative" />
                                <input
                                    name="selected"
                                    v-model="form.is_reusable"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                <FormError :value="form.errors.is_reusable" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$tc('messages.languages', 1)" for="locale" />
                                <FormSelect required v-model="form.locale" name="locale" :options="languages" />
                                <FormError :value="form.errors.locale" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-6">
                            <div class="mt-1">
                                <FormLabel :value="$t('messages.is_calculator_allowed')" for="is_calculator_allowed" />
                                <FormSelect
                                    v-model="form.is_calculator_allowed"
                                    name="locale"
                                    :options="calculatorOptions"
                                />
                                <FormError :value="form.errors.is_calculator_allowed" class="mt-2" />
                            </div>
                        </div>

                        <div class="sm:col-span-12">
                            <FormLabel :value="$tc('messages.tags', 2)" for="is_negative" />
                            <TagsInput v-model="form.tags"></TagsInput>
                            <FormError :value="form.errors.tags" class="mt-2" />
                        </div>
                    </div>

                    <div class="flex-none flex justify-end mt-5 space-x-2 bg-gray-50 pt-2 px-2">
                        <AppButton
                            element="button"
                            type="submit"
                            @click="another = false"
                            :disabled="form.processing"
                            desired-action
                        >
                            {{ $t('messages.save') }}
                            <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                        </AppButton>
                        <!--                        <AppButton element="button" type="submit" @click="another = true" :disabled="form.processing" desired-action>-->
                        <!--                            {{ $t('messages.save_and_another') }}-->
                        <!--                            <span v-if="form.processing" class="ml-1 btn-spinner"></span>-->
                        <!--                        </AppButton>-->
                    </div>
                </form>
            </SlideOver>
        </div>
    </div>
</template>

<script>
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import TagsInput from '@/components/Quiz/TagsInput.vue';
import FormSmallNumberRangeInput from '@/components/Forms/FormSmallNumberRangeInput.vue';
import QuizSpecificOptionsForm from '@/components/Quiz/QuizSpecificOptionsForm.vue';
import cloneDeep from 'lodash/cloneDeep';
import SlideOver from '@/components/SlideOver.vue';
import QuestionIcon from '@/components/QuestionIcon.vue';
import LatexDisplay from '@/components/LatexDisplay.vue';
import { Tippy } from 'vue-tippy';
import AppWell from '@/Shared/AppWell.vue';
import { IconCopy, IconHelpCircle } from '@tabler/icons-vue';
import Editor from '@/components/Forms/RichTextEditor/Editor.vue';
import QuestionSharedImageSuggestions from '@/components/Quiz/QuestionSharedImageSuggestions.vue';

export default {
    emits: ['close'],
    name: 'QuestionCreateFormPanel',
    components: {
        QuestionSharedImageSuggestions,
        Editor,
        IconHelpCircle,
        IconCopy,
        AppWell,
        Tippy,
        LatexDisplay,
        QuestionIcon,
        SlideOver,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        AppButton,
        TagsInput,
        FormSmallNumberRangeInput,
        QuizSpecificOptionsForm,
    },
    props: {
        meta: {
            required: true,
            type: Object,
        },
        defaults: {
            required: false,
            type: Object,
        },
        question: {
            required: false,
            type: Object,
        },
        canAddQuestions: {
            required: false,
            type: Boolean,
            defaults: true,
        },
    },
    data() {
        return {
            open: false,
            another: false,
            languages: ['ca', 'es', 'en'].map((l) => {
                return {
                    value: l,
                    text: this.$t(`locales.${l}`),
                };
            }),
            calculatorOptions: [
                {
                    value: 0,
                    text: this.$t(`messages.calculator_is_not_allowed`),
                },
                {
                    value: 1,
                    text: this.$t(`messages.calculator_is_allowed`),
                },
            ],
            questionTypeGroups: [
                {
                    label: ' ',
                    options: [
                        {
                            value: 'multiple-choice',
                            text: this.$t('messages.question_type_multiple_choice'),
                        },
                        {
                            value: 'short-answer',
                            text: this.$t('messages.question_type_short_answer'),
                        },
                        {
                            value: 'true-false',
                            text: this.$t('messages.question_type_true_false'),
                        },
                        {
                            value: 'order-phrase',
                            text: this.$t('messages.question_type_order_phrase'),
                        },
                        {
                            value: 'order-elements',
                            text: this.$t('messages.question_type_order_elements'),
                        },
                        {
                            value: 'multiple-answer',
                            text: this.$t('messages.question_type_multiple_answer'),
                        },
                    ],
                },
                {
                    label: this.$t('messages.category_math'),
                    options: [
                        {
                            value: 'math-fraction',
                            text: this.$t('messages.question_type_math_fraction'),
                        },
                        {
                            value: 'math-number-with-tolerance',
                            text: this.$t('messages.question_type_math_number_with_tolerance'),
                        },
                        {
                            value: 'math-xyz-answers',
                            text: this.$t('messages.question_type_math_xyz_answers'),
                        },
                        {
                            value: 'math-multiple-magnitudes',
                            text: this.$t('messages.question_type_math_multiple_magnitudes'),
                        },
                        {
                            value: 'math-prime-factors',
                            text: this.$t('messages.question_type_math_prime_factors'),
                        },
                        {
                            value: 'math-polynomial-operation',
                            text: this.$t('messages.question_type_math_polynomial_operation'),
                        },
                        {
                            value: 'math-latex-answer',
                            text: this.$t('messages.question_type_math_latex_answer'),
                        },
                        // { question_create_form_panel_question_type_option }
                    ],
                },
            ],
            form: this.$inertia.form({
                type: null,
                title: '',
                hint: '',
                correct_answer: '',
                description: '',
                subject_id: '',
                locale: this.defaults.locale,
                weight: 1,
                difficulty: 3,
                is_reusable: false,
                image: null,
                shared_image_name: null,
                is_latex: false,
                is_latex_answers: false,
                grade_level_ids: [],
                tags: [],
                options: {},
                is_calculator_allowed: null,
            }),
        };
    },
    methods: {
        dismiss() {
            this.$emit('close');
        },
        openQuestionFor(type) {
            this.form.type = type;
            this.open = true;
            this.focusTitle();
        },
        focusTitle() {
            this.$nextTick(function () {
                this.$refs.title.focusInput();
            });
        },
        openCopyOfQuestion(question) {
            this.form.title = question.title;
            this.form.hint = question.hint;
            this.form.correct_answer = question.correct_answer;
            this.form.description = question.description;
            this.form.subject_id = question.subject_id;
            this.form.locale = question.locale;
            this.form.weight = question.weight;
            this.form.difficulty = question.difficulty;
            this.form.is_reusable = question.is_reusable;
            this.form.is_latex = question.is_latex;
            this.form.is_latex_answers = question.is_latex_answers;
            this.form.image = question.image;
            this.form.shared_image_name = question.shared_image_name;
            this.form.grade_level_ids = question.grade_level_ids;
            this.form.tags = question.tags;
            this.form.options = cloneDeep(question.options);

            this.openQuestionFor(question.type);
        },
        submit() {
            const url = this.another ? this.meta.links.store_and_another : this.meta.links.store;
            this.form.post(url, {
                forceFormData: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.open = false;
                    this.form.reset(
                        'type',
                        'title',
                        'correct_answer_index',
                        'description',
                        'options',
                        'is_latex',
                        'difficulty',
                        'hint',
                        'image',
                        'is_calculator_allowed',
                        'is_latex',
                        'is_latex_answers',
                        'shared_image_name',
                    );
                },
            });
        },
    },
    computed: {
        canHaveLatexAnswers() {
            return ['multiple-choice', 'math-multiple-magnitudes', 'math-xyz-answers'].includes(this.form.type);
        },
    },
};
</script>
